import { Link } from "react-router-dom";
import { Lock, Sparkles, ArrowUpRight } from "lucide-react";
import { Button } from "./ui/button";

/**
 * PaywallCard — shown in place of a locked module/feature for users whose
 * active plan does not include it. Drives upsell to Essentials/Pro.
 *
 * Props:
 *  - moduleLabel: "Contabilidad", "Inventario", "Previsiones IA"… (required)
 *  - description: short pitch of what the module does for the user
 *  - requiredPlan: "Essentials" | "Pro" | "Premium" (default Essentials)
 *  - ctaLabel: override CTA text
 *  - benefits: array of bullet benefits ("Ahorra 4h/semana", etc.)
 */
export default function PaywallCard({
  moduleLabel,
  description,
  requiredPlan = "Essentials",
  ctaLabel,
  benefits = [],
}) {
  const labelCta = ctaLabel || `Desbloquear con ${requiredPlan}`;
  return (
    <div
      data-testid={`paywall-card-${(moduleLabel || "").toLowerCase().replace(/\s+/g, "-")}`}
      className="relative mx-auto max-w-2xl rounded-2xl border border-border/80 bg-card p-8 shadow-sm dark:border-slate-800 dark:bg-slate-900"
    >
      <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-3 py-1 text-xs font-semibold text-white shadow">
        <Sparkles className="h-3.5 w-3.5" />
        Disponible desde {requiredPlan}
      </div>

      <div className="flex items-start gap-5">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-muted text-muted-foreground dark:bg-slate-800">
          <Lock className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h2 className="text-2xl font-semibold tracking-tight text-foreground dark:text-slate-100">
            {moduleLabel}
          </h2>
          {description ? (
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground dark:text-slate-400">
              {description}
            </p>
          ) : null}
        </div>
      </div>

      {benefits.length ? (
        <ul className="mt-6 space-y-2.5 border-t border-slate-100 pt-5 text-sm text-foreground dark:border-slate-800 dark:text-slate-300">
          {benefits.map((b, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
        <Link to="/app/subscription" data-testid="paywall-upgrade-link" className="w-full sm:w-auto">
          <Button
            size="lg"
            className="w-full gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:from-violet-500 hover:to-fuchsia-500"
            data-testid="paywall-upgrade-btn"
          >
            {labelCta}
            <ArrowUpRight className="h-4 w-4" />
          </Button>
        </Link>
        <Link
          to="/app/subscription"
          className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-slate-700 hover:underline dark:text-slate-400 dark:hover:text-slate-200"
          data-testid="paywall-compare-link"
        >
          Ver todos los planes
        </Link>
      </div>
    </div>
  );
}
